import { Checkbox, Divider, Flex, Form } from 'antd';
import { FormProps } from 'antd/lib';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import InfoTooltip from '@/components/general/InfoTooltip';
import CompanyContact from '@/model/General/CompanyContact';

interface ReceptionContactFormProps {
  formProps: FormProps;
  companyContactData: { selectedCompany?: CompanyContact[]; ticketCompany?: CompanyContact[] };
  loadingCompanyContactData: boolean;
  sendToClient: boolean;
  setSendToClient: Function;
}

const ReceptionContactForm = (props: ReceptionContactFormProps) => {
  const { t } = useTranslation();
  const {
    formProps,
    companyContactData,
    loadingCompanyContactData,
    sendToClient,
    setSendToClient,
  } = props;
  const { form } = formProps;
  const company = Form.useWatch('company', form);
  const companyContact = Form.useWatch('company_contact', form);
  const [companyContactWarning, setCompanyContactWarning] = useState<string>();

  // Seteo de warning si no tiene contactos
  useEffect(() => {
    if (companyContactData.selectedCompany === undefined || loadingCompanyContactData) {
      return;
    }

    if (company && companyContactData.selectedCompany?.length === 0) {
      form?.setFields([
        {
          name: 'company',
          warnings: [t('La empresa o técnico no tiene un contacto asignado')],
        },
      ]);
    } else {
      form?.setFields([
        {
          name: 'company',
          warnings: [],
        },
      ]);
    }
  }, [company, companyContactData.selectedCompany, t, form, loadingCompanyContactData]);

  //Warning si el contacto no tiene email
  useEffect(() => {
    if (!companyContactData || !companyContact) {
      setCompanyContactWarning(undefined);

      return;
    }

    const contactId = typeof companyContact === 'string' ? Number(companyContact) : companyContact;
    const selectedContact = [
      ...(companyContactData.selectedCompany ?? []),
      ...(companyContactData.ticketCompany ?? []),
    ].find(contact => contact.id === contactId);

    if (selectedContact && !selectedContact.email) {
      setCompanyContactWarning(t('El contacto seleccionado no tiene un email asignado'));
    } else {
      setCompanyContactWarning(undefined);
    }
  }, [companyContact, companyContactData, t]);

  useEffect(() => {
    if (!sendToClient) {
      form?.setFieldValue('company_contact', undefined);
    }
  }, [sendToClient, form]);

  // Armado de opciones de contacto
  const hasAvailableContacts =
    !!companyContactData.selectedCompany?.length || !!companyContactData.ticketCompany?.length;
  const formatContactOption = (contact: CompanyContact) => ({
    label: (
      <>
        {contact.name}
        {contact.email ? (
          <>
            <Divider type="vertical" /> {contact.email}
          </>
        ) : null}
      </>
    ),
    value: contact.id,
  });
  const contactOptions = useMemo(() => {
    const groupedOptions: any[] = [];

    if (companyContactData.selectedCompany?.length) {
      groupedOptions.push({
        label: t('Contactos de la empresa seleccionada'),
        options: companyContactData.selectedCompany.map(formatContactOption),
      });
    }

    if (companyContactData.ticketCompany?.length) {
      groupedOptions.push({
        label: t('Contactos de la empresa asignada al ticket'),
        options: companyContactData.ticketCompany.map(formatContactOption),
      });
    }

    if (groupedOptions.length === 0) {
      const fallbackContacts =
        companyContactData.selectedCompany ?? companyContactData.ticketCompany ?? [];

      return fallbackContacts.map(formatContactOption);
    }

    return groupedOptions;
  }, [companyContactData.selectedCompany, companyContactData.ticketCompany, t]);
  const companyContactRules = useMemo(() => {
    if (!sendToClient) return [];

    return [
      {
        required: true,
        message: t('Debe seleccionar un contacto'),
      },
      {
        validator: () => {
          if (companyContactWarning) {
            return Promise.reject(new Error(companyContactWarning));
          }

          return Promise.resolve();
        },
      },
    ];
  }, [companyContactWarning, sendToClient, t]);

  return (
    <>
      <Form.Item label={t('Seleccione un contacto de la lista')} required={sendToClient}>
        <Flex vertical gap={4}>
          <Form.Item
            name={'company_contact'}
            rules={companyContactRules}
            {...{ noStyle: companyContactWarning ? false : true }}
            {...(sendToClient
              ? {
                  validateStatus: companyContactWarning ? 'error' : undefined,
                  help: companyContactWarning || undefined,
                }
              : {})}>
            <AntdSelect
              loading={loadingCompanyContactData}
              disabled={!company || !hasAvailableContacts || !sendToClient}
              options={contactOptions}
              allowClear
              showSearch
            />
          </Form.Item>
          <div style={{ margin: '4px 0px' }}>
            <Checkbox
              disabled={!company || !hasAvailableContacts}
              checked={sendToClient}
              onChange={e => {
                setSendToClient(e.target.checked);
              }}
            />{' '}
            {t('Enviar presupuesto al cliente')}{' '}
            <InfoTooltip
              iconStyle={{ marginLeft: 0 }}
              type="info"
              tooltipText={t(
                'Si no se selecciona un contacto, se enviara el presupuesto al responsable de servicio al cliente',
              )}
            />
          </div>
        </Flex>
      </Form.Item>
    </>
  );
};

export default ReceptionContactForm;
